import { CircularProgress, Container, Grid } from "@mui/material";
import { dripOfferAssets } from "assets/cdn";
import CountdownV2 from "components/CountdownV2";
import PageTopNav, { ISectionMenuItem } from "components/PageTopNav";
import PageTopNavV2 from "components/PageTopNavV2";
import {
	useEnvContext,
	useGoogleAnalyticsContext,
	useWebStoreContext
} from "contexts";
import { IShopReward, IShopRewardItem } from "contexts/WebStoreProvider/types";
import { FeaturesFlags } from "helpers";
import { useAppDispatch, useImagePath } from "hooks";
import useBreakpoints from "hooks/useBreakpoints";
import useNewDay from "hooks/useNewDay";
import { Config, PurchaseOfferSingleItem, WsDialogs } from "interfaces";
import { DripOfferResponse, Node, Tracker } from "interfaces/drip-offer";
import { FeatureType } from "interfaces/feature-tracker";
import { debounce } from "lodash";
import { ScrollToContentButton } from "pages/LandingPage/lib/Hero/lib/ScrollToContentButton";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	featureTrackerAPI,
	offersApi,
	useClaimDripOfferRewardMutation,
	useGetFeatureTrackersQuery
} from "redux/apis";
import { onOpenPopUp } from "redux/slices";
import { useDialogModalRedux } from "redux/slices/dialog/hooks/useDialogModalRedux";
import { getVariantImage } from "helpers/getVariantImage";
import { InfoIcon } from "utils/svgIcons";
import {
	getAvailablePrizes,
	getNodeItemData,
	handleTicketVariant,
	handleTitleImageVariant,
	sortTrackersByIndex
} from "./hooks";
import {
	DisplayFirstNode,
	DisplayNode,
	DripOfferSkeleton,
	Offer,
	SaleInfo
} from "./lib";
import {
	Background,
	Button,
	DripOfferContainer,
	InfoButton,
	Title,
	TopBrush,
	timerStyle,
	timerStyleMobile
} from "./styles";
import { getCount, getDisplay, getTimeLeft } from "utils/time";
import { PrivateRoute } from "routes/PrivateRoute";

const DripOffer = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { getImage } = useImagePath("cdn");
	const topBrush = getImage(dripOfferAssets.topBrush);
	const ref = useRef<HTMLDivElement>(null);

	const {
		authToken,
		handleErrorResponse,
		nowTime,
		postUserMetadata,
		store,
		suppressOrderConfirmation,
		userTracker,
		wsStoreBuy,
		openLoginToContinueDialog
	} = useWebStoreContext();
	const { openDialog } = useDialogModalRedux();
	const { isFeatureFlagEnabled } = useEnvContext();
	const { isSmUp, isSmDown, isMdDown } = useBreakpoints();
	const { reportEvent, reportPlaygamiEvent } = useGoogleAnalyticsContext();

	const [ClaimDripOfferReward, { isLoading: IsClaimLoading }] =
		useClaimDripOfferRewardMutation();

	const [sectionMenuData] = useState<ISectionMenuItem[]>([]);

	const { newDay } = useNewDay();
	const {
		data: dripOfferData,
		refetch: refetchDripOffer,
		isSuccess,
		isFetching,
		isError
	}: {
		data?: DripOfferResponse;
		refetch: () => void;
		isSuccess: boolean;
		isFetching: boolean;
		isError: boolean;
	} = useGetFeatureTrackersQuery(
		{
			FeatureType: FeatureType.DRIP_OFFER,
			isAuthenticated: !!userTracker
		},
		{
			skip: !isFeatureFlagEnabled(FeaturesFlags.DRIP_OFFER) || !userTracker,
			refetchOnFocus: true
		}
	);

	const { data: purchaseOffers } = offersApi.endpoints.getOffers.useQueryState({
		offerType: "singleItem",
		isAuthenticated: !!authToken?.value
	});

	const [firstOffer, setFirstOffer] = useState<
		Array<{
			offer: PurchaseOfferSingleItem;
			items: IShopRewardItem[];
		}>
	>([]);
	const [availableNode, setAvailableNode] = useState<Node[]>([]);
	const [hideScrollIcon, setHideScrollIcon] = useState<boolean>(false);
	const [sortedTrackersIndex, setSortedTrackersIndex] = useState<number[]>([]);

	const { trackers } = dripOfferData || {};

	const handleEnd = () => {
		setTimeout(() => {
			refetchDripOffer();
		}, 1000);
	};

	const invalidateFeatureTracker = () => {
		dispatch(
			featureTrackerAPI.util.invalidateTags([
				{
					type: "featureTrackerTag",
					id: FeatureType.DRIP_OFFER
				}
			])
		);
	};

	const onSetAvailableNodes = (n: Node) => {
		setAvailableNode((nodes) => [...nodes, n]);
	};

	const onClickBuy = async (
		tracker: Tracker,
		offer?: PurchaseOfferSingleItem,
		node?: Node
	) => {
		if (!authToken?.value) {
			return;
		}

		const { offerPurchaseDate, nodes, claimEndDate, eventId, offerEndDate } =
			tracker;

		if (offerPurchaseDate && node) {
			await ClaimDripOfferReward({
				eventId: node.eventId,
				nodeIndex: node.nodeIndex
			})
				.unwrap()
				.then(() => {
					if (getAvailablePrizes(nodes) === 1) {
						dispatch(onOpenPopUp("drip-offer-end"));
					} else {
						dispatch(onOpenPopUp("drip-offer-reward"));
					}
					/* navigate("/shop");
					dispatch(onScrollToShopSection("Featured Deals")); */
					//refetchDripOffer();
					const index = availableNode.findIndex((n) => n.eventId === eventId);
					if (index !== -1) {
						const updatedNodes = availableNode.splice(index, 1);
						setAvailableNode(updatedNodes);
					}
					if (store) {
						const timeRemaining = getCount(nowTime, claimEndDate);
						const timeToDisplay = getDisplay(timeRemaining);
						const timeLeft = getTimeLeft(nowTime, offerEndDate, "seconds");
						const rewardType: IShopRewardItem[] | null = getNodeItemData(
							store,
							node
						);
						reportEvent("drip_offer", {
							source: "claim_free_daily_reward",
							reward_id: node.rewardId,
							reward_type: `${rewardType?.[0].id}_${rewardType?.[0].value}`,
							location: "special_offer_section",
							time_to_claim: timeToDisplay
						});
						reportPlaygamiEvent("tp.web_frontend", {
							event_name: "drip_offer",
							event_type: "claim_free_daily_reward",
							action: "feature_rendered",
							source: "drip_offer",
							reward_id: node.rewardId,
							reward_type: `${rewardType?.[0].id}_${rewardType?.[0].value}`,
							section_name: location.pathname.replace("/", ""),
							time_left: timeLeft,
							type: "drip_offer_collect_page"
						});
					}
				})
				.catch(() => {
					const response = {
						data: {
							errorCode: "The prize has already been claimed!",
							errorMessage: ""
						}
					};
					handleErrorResponse({ response: response });
				})
				.finally(() => {
					invalidateFeatureTracker();
				});
		} else if (!offerPurchaseDate && offer) {
			const { id, price, rewardId } = offer;

			wsStoreBuy({ offerId: id, drip_offer_event_id: eventId })
				.then((invoice) => {
					if (invoice) {
						const timestampMs = new Date().getTime();
						openDialog(WsDialogs.XSOLLA_PURCHASE, {
							xsollaToken: invoice.xsollaToken,
							xsollaHost: invoice.xsollaHost,
							invoice,
							suppressOrderConfirmation,
							source: "drip-offer",
							pricePoint: price,
							postUserMetadata,
							onPurchaseStarted: (data) => {
								const minutesLeft = getTimeLeft(nowTime, offerEndDate);
								reportEvent("purchase_started", {
									section_name: "special_offer",
									offer_id: id,
									price_in_cents: price * 100,
									reward_id: rewardId,
									invoice_id: invoice?.id,
									paystation_id: data?.payStation
								});
								reportPlaygamiEvent("tp.web_ga.purchase_flow", {
									event_name: "purchase_started",
									event_type: "web_paystation_purchase",
									action: "purchase_started",
									section_name: "special_offer",
									purchase_flow_id: data?.payStation,
									session_engaged: authToken?.value ? "1" : "0",
									price_in_cents: Math.round(invoice.price * 100),
									page_location: window.location.href,
									engagement_time_msec: (
										Number(data?.data?.milliseconds) - timestampMs
									).toString(),
									reward_id: invoice?.rewardId,
									offer_id: invoice?.offerId,
									ignore_referrer:
										process.env.REACT_APP_WITH_DEBUG === "true"
											? "true"
											: "false",
									minutes_left: minutesLeft,
									page_title: location.pathname.replace("/", ""),
									page_referrer: invoice?.invoiceMetadata?.referer,
									invoice_id: invoice?.id
								});
							},
							onPurchaseSuccess: (data) => {
								reportEvent("purchase_success", {
									invoice_id: invoice?.id
								});
								reportPlaygamiEvent("tp.web_ga.purchase_flow", {
									event_name: "purchase_success",
									event_type: "web_paystation_purchase",
									action: "purchase_success",
									section_name: "special_offer",
									purchase_flow_id: data?.version,
									session_engaged: authToken?.value ? "1" : "0",
									price_in_cents: Math.round(invoice.price * 100),
									page_location: window.location.href,
									engagement_time_msec: (
										Number(data?.milliseconds) - timestampMs
									).toString(),
									reward_id: invoice?.rewardId,
									offer_id: invoice?.offerId,
									ignore_referrer:
										process.env.REACT_APP_WITH_DEBUG === "true"
											? "true"
											: "false",
									page_title: location.pathname.replace("/", ""),
									page_referrer: invoice?.invoiceMetadata?.referer,
									invoice_id: invoice?.id,
									transaction_id: data?.purchase_invoice_id?.toString()
								});
								refetchDripOffer();
								dispatch(onOpenPopUp("drip-offer-reward"));
							},
							cbClose: () => {
								reportEvent("dialog_x_closed", {
									source: "xsolla_dialog",
									page_location: location.pathname
								});
							}
						});
					}
				})
				.finally(() => {
					invalidateFeatureTracker();
				});
		}
	};

	const handleScroll = () => {
		if (window.scrollY >= 50) {
			setHideScrollIcon(true);
		} else {
			setHideScrollIcon(false);
		}
	};

	const debouncedHandleScroll = debounce(handleScroll, 100);

	useEffect(() => {
		addEventListener("scroll", debouncedHandleScroll);
		return () => {
			removeEventListener("scroll", debouncedHandleScroll);
		};
	}, []);

	useEffect(() => {
		if (trackers && trackers.length > 0) {
			setSortedTrackersIndex(sortTrackersByIndex(trackers));
		}
	}, [trackers?.length]);

	useEffect(() => {
		!userTracker && openLoginToContinueDialog();
	}, [userTracker]);

	useEffect(() => {
		if ((isSuccess && !dripOfferData?.trackers) || isError) {
			navigate("/");
		}
	}, [isSuccess, isError, dripOfferData]);

	useEffect(() => {
		if (newDay) {
			refetchDripOffer();
		}
	}, [newDay]);

	useEffect(() => {
		if (isFetching) {
			setAvailableNode([]);
		}
	}, [isFetching]);

	// maybe change it for useRewardsById
	useEffect(() => {
		if (trackers && trackers.length > 0 && purchaseOffers) {
			const offers = purchaseOffers?.offers ?? [];
			const firstOfferList: Array<{
				offer: PurchaseOfferSingleItem;
				items: IShopRewardItem[];
			}> = [];
			trackers.forEach((tracker: Tracker) => {
				const offer: PurchaseOfferSingleItem = offers.find(
					(offer: Config) => offer.id === tracker.offerId
				);
				if (offer) {
					if (store) {
						const reward = store.rewards[offer.rewardId];
						if (!reward) {
							console.error("reward: ", reward);
							return;
						}
						const items: IShopRewardItem[] = Object.keys(reward.items).map(
							(key) => {
								const { imageUrl = "" } = {
									...store.rewardTypes.filter(
										(t: IShopReward) => t.id === key
									)[0]
								};
								const rewardItem = reward.items;
								return {
									id: key,
									value: rewardItem[key as keyof typeof rewardItem],
									imageUrl
								};
							}
						);
						firstOfferList.push({ offer: offer, items: items });
					}
				}
			});
			setFirstOffer(firstOfferList);
		}
	}, [purchaseOffers, trackers, store?.rewards]);

	if (trackers && trackers.length > 0 && firstOffer.length > 0) {
		return (
			<PrivateRoute
				isEnabled={
					!!userTracker && isFeatureFlagEnabled(FeaturesFlags.DRIP_OFFER)
				}
			>
				{isFeatureFlagEnabled(FeaturesFlags.UI_V2) ? (
					<PageTopNavV2 sectionMenuData={sectionMenuData} />
				) : (
					<PageTopNav sectionMenuData={sectionMenuData} />
				)}
				<>
					{trackers.length > 1 && (
						<ScrollToContentButton
							sectionContentRef={ref}
							offsetNumber={-87}
							sx={{
								display: { xs: "block", sm: "none" },
								position: "fixed",
								bottom: 15,
								left: "calc(50% - 16px)",
								zIndex: 3,
								svg: {
									width: "32px",
									height: "32px"
								},
								transition: "all 0.3s ease",
								opacity: hideScrollIcon ? 0 : 0.6,
								"&:hover": { opacity: hideScrollIcon ? 0 : 1 },
								pointerEvents: hideScrollIcon ? "none" : "auto"
							}}
						/>
					)}
					{sortedTrackersIndex.map((sortedIdx, index: number) => {
						const tracker = trackers[sortedIdx];
						if (tracker && Object.keys(tracker).length > 0) {
							const {
								claimEndDate,
								dateTimes,
								isFinished,
								nodes,
								offerBackgroundImageUrl,
								offerEndDate,
								offerPurchaseDate,
								offerTicketImageUrl,
								saleInfoImageUrl,
								titleImageUrl,
								eventId,
								claimDurationDays
							} = tracker;

							return (
								<DripOfferContainer
									ref={index === 1 ? ref : null}
									key={tracker.eventId}
									largeTicket={getVariantImage(
										offerPurchaseDate ? saleInfoImageUrl : offerTicketImageUrl,
										"{drip_offer_variant}",
										isSmDown ? "small" : "large"
									)}
								>
									{index > 0 && (
										<TopBrush>
											<img src={topBrush} />
										</TopBrush>
									)}
									<Background
										background={getVariantImage(
											offerBackgroundImageUrl,
											"{drip_offer_variant}",
											isSmDown ? "small" : "large"
										)}
										firstBackground={index === 0 ? true : false}
									/>
									<Container
										sx={{
											position: "relative",
											top: { xs: "-0.4em", sm: 0 },
											width: { xs: "25.5em", sm: "94%", md: "40em" },
											padding: { xs: 0 }
										}}
									>
										<Grid container>
											<Grid
												item
												xs={12}
												textAlign="center"
												zIndex={1}
												mb={{ xs: 2, sm: 0.5, md: 0 }}
												position="relative"
											>
												<CountdownV2
													endTime={claimEndDate ?? offerEndDate}
													style={isSmDown ? timerStyleMobile : timerStyle}
													startTime={nowTime}
													text={
														claimEndDate ? "Time to claim: " : "Offer ends in: "
													}
													labelStyle={{ color: "#fff", marginLeft: 6 }}
													onEnd={() => {
														handleEnd();
													}}
												/>
												<Title
													src={handleTitleImageVariant(
														titleImageUrl,
														!!offerPurchaseDate
													)}
													alt="Tiki Travels"
												/>
												<InfoButton
													aria-label="open info popup"
													onClick={() => {
														dispatch(
															onOpenPopUp(`drip-offer-info::${sortedIdx}`)
														);
													}}
												>
													<InfoIcon width={30} height={30} />
												</InfoButton>
												{isSmUp && isMdDown && !offerPurchaseDate && (
													<Grid item position="absolute" bottom={0} left={0}>
														<Grid item>
															<h3>Get this now:</h3>
														</Grid>
													</Grid>
												)}
											</Grid>
											{!offerPurchaseDate ? (
												<Offer firstOffer={firstOffer[sortedIdx]} />
											) : (
												<>
													{isSmUp && (
														<SaleInfo
															nodes={nodes}
															localTime={dateTimes.local}
															nowTime={nowTime}
															endTime={claimEndDate}
															claimDurationDays={claimDurationDays}
														/>
													)}
												</>
											)}
											<Grid item xs={12}>
												<Grid
													container
													direction="row"
													spacing={1}
													mb={1.2}
													justifyContent={{
														xs: "space-between"
													}}
												>
													{firstOffer?.[sortedIdx] && offerPurchaseDate && (
														<DisplayFirstNode
															items={firstOffer[sortedIdx].items}
															background={handleTicketVariant(
																nodes[0].rewardBackgroundImageUrl,
																false
															)}
														/>
													)}
													{nodes.map((node) => (
														<DisplayNode
															key={node.rewardId}
															node={node}
															onSetAvailableNode={onSetAvailableNodes}
															changeSize={!!offerPurchaseDate}
															availableNode={availableNode?.[index]}
															offerPurchaseDate={offerPurchaseDate}
														/>
													))}
													{offerPurchaseDate && isSmDown && (
														<SaleInfo
															nodes={nodes}
															localTime={dateTimes.local}
															nowTime={nowTime}
															endTime={claimEndDate}
															claimDurationDays={claimDurationDays}
															isSmall={true}
														/>
													)}
												</Grid>
											</Grid>
											<Grid
												item
												xs={12}
												mb={{
													xs: index === sortedTrackersIndex.length - 1 ? 8 : 11,
													sm: index === sortedTrackersIndex.length - 1 ? 8 : 14
												}}
												display="flex"
												justifyContent="center"
											>
												{!isFinished && (
													<Button
														disabled={
															isFetching ||
															(offerPurchaseDate &&
																availableNode.findIndex(
																	(n) => n.eventId === eventId
																) === -1) ||
															IsClaimLoading
														}
														onClick={() =>
															onClickBuy(
																tracker,
																firstOffer[sortedIdx]?.offer,
																availableNode.find((n) => n.eventId === eventId)
															)
														}
													>
														{isFetching ? (
															<CircularProgress
																size={24}
																style={{
																	color: "#7b7b7b"
																}}
															/>
														) : offerPurchaseDate ? (
															availableNode.findIndex(
																(n) => n.eventId === eventId
															) !== -1 ? (
																"COLLECT"
															) : (
																"Come Back Tomorrow"
															)
														) : (
															`${firstOffer[sortedIdx]?.offer.price} USD`
														)}
													</Button>
												)}
											</Grid>
										</Grid>
									</Container>
								</DripOfferContainer>
							);
						} else {
							return <DripOfferSkeleton key={index} />;
						}
					})}
				</>
			</PrivateRoute>
		);
	} else {
		return (
			<PrivateRoute
				isEnabled={
					!!userTracker && isFeatureFlagEnabled(FeaturesFlags.DRIP_OFFER)
				}
			>
				<DripOfferSkeleton />
			</PrivateRoute>
		);
	}
};

export default DripOffer;
